import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, MapPin, Menu, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Layout";
import { api } from "@/lib/api";
import PublicCatalog, { SectionHeading } from "@/pages/PublicCatalog";
import { PublicMemories } from "@/components/PublicMemories";
import { PublicAlbums, usePublicAlbums } from "@/components/PublicAlbums";
import PublicInterest from "@/pages/PublicInterest";

const NAV_LINKS = [
  { href: "#inicio", label: "Início" },
  { href: "#historia", label: "História" },
  { href: "#20anos", label: "20 Anos" },
  { href: "#cursos", label: "Cursos" },
  { href: "#valores", label: "Valores" },
  { href: "#professores", label: "Professores" },
  { href: "#horarios", label: "Horários" },
  { href: "#links-uteis", label: "Links Úteis" },
  { href: "#contato", label: "Contato" },
];

function PublicHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-zinc-800 bg-[#0A0A0A]/95 backdrop-blur-md" data-testid="public-header">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <a href="#inicio" aria-label="Instituto Musical Jacaré">
          <Logo dark />
        </a>
        <nav className="hidden items-center gap-6 lg:flex" data-testid="public-nav">
          {NAV_LINKS.map((l) =>
            l.href.startsWith("#") ? (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-zinc-300 transition-colors hover:text-primary"
                data-testid={`public-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.href}
                to={l.href}
                className="text-sm font-medium text-zinc-300 transition-colors hover:text-primary"
                data-testid={`public-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="border-zinc-700 bg-transparent font-semibold text-white transition-colors hover:bg-zinc-800 hover:text-white"
            data-testid="header-login-button"
          >
            <Link to="/acesso">Entrar</Link>
          </Button>
          <Button
            asChild
            className="hidden bg-primary font-semibold text-primary-foreground hover:bg-primary/90 sm:inline-flex"
            data-testid="header-cta-button"
          >
            <a href="#interesse">Quero estudar</a>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 hover:text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Abrir menu"
            data-testid="public-mobile-menu-button"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-zinc-800 bg-[#0A0A0A] px-4 py-3 lg:hidden" data-testid="public-mobile-nav">
          {NAV_LINKS.map((l) =>
            l.href.startsWith("#") ? (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block py-2.5 text-sm font-medium text-zinc-300 transition-colors hover:text-primary"
                data-testid={`public-mobile-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setOpen(false)}
                className="block py-2.5 text-sm font-medium text-zinc-300 transition-colors hover:text-primary"
                data-testid={`public-mobile-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </Link>
            )
          )}
          <a
            href="#interesse"
            onClick={() => setOpen(false)}
            className="mt-2 block bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground"
            data-testid="mobile-nav-cta-button"
          >
            Quero estudar
          </a>
        </nav>
      )}
    </header>
  );
}

function Hero({ courses }) {
  return (
    <section id="inicio" className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full border-[24px] border-primary/15" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-80 w-80 rounded-full border-[16px] border-primary/10" />
      <div className="pointer-events-none absolute right-24 top-1/3 hidden h-20 w-20 rounded-full bg-primary/20 lg:block" />
      <img
        src="https://images.unsplash.com/photo-1510915361894-db8b60106cb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhY291c3RpYyUyMGd1aXRhciUyMGNsb3NlJTIwdXAlMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg4Njk1MTczfDA&ixlib=rb-4.1.0&q=85"
        alt="Violão em close com luz quente — imagem ilustrativa, não é uma foto do IMJ"
        className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-2/5 object-cover opacity-15 [mask-image:linear-gradient(to_left,black,transparent)] lg:block"
      />
      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
        <div className="flex flex-wrap items-center gap-3">
          <span className="h-px w-10 bg-primary" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Desde 2006 · Cabreúva — SP</span>
          <span
            className="border border-primary/60 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary"
            data-testid="hero-anniversary-badge"
          >
            2006 — 2026 · 20 anos
          </span>
        </div>
        <h1
          className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tighter sm:text-5xl lg:text-6xl"
          data-testid="hero-title"
        >
          A música que <span className="text-primary">transforma vidas</span> começa aqui.
        </h1>
        <p className="mt-6 max-w-xl text-base text-zinc-300 sm:text-lg">
          Há 20 anos o Instituto Musical Jacaré forma músicos em Cabreúva com aulas individuais, professores dedicados
          e um ensino feito sob medida para cada aluno.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Button
            asChild
            size="lg"
            className="bg-primary px-8 font-semibold text-primary-foreground hover:bg-primary/90"
            data-testid="hero-cta-enroll"
          >
            <a href="#interesse">Quero me matricular</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-zinc-600 bg-transparent px-8 font-semibold text-white hover:bg-zinc-800 hover:text-white"
            data-testid="hero-cta-courses"
          >
            <a href="#cursos">Conhecer os cursos</a>
          </Button>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-px bg-zinc-800 sm:grid-cols-4" data-testid="hero-stats">
          {[
            { v: "20", l: "anos de história" },
            { v: String(courses.length || "—"), l: "cursos de instrumento" },
            { v: "1:1", l: "aulas individuais" },
            { v: "R$ 0", l: "taxa de matrícula" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0A0A0A] px-4 py-5">
              <p className="font-heading text-2xl font-extrabold text-primary sm:text-3xl">{s.v}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-zinc-400">{s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LinksUteis() {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    api.get("/public/daily-links").then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, []);
  if (!links || links.length === 0) return null;
  return (
    <section id="links-uteis" className="border-b border-border bg-white py-10 lg:py-12" data-testid="home-links-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="h-px w-10 bg-primary" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Atalhos do dia a dia</span>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between gap-3 border border-border bg-secondary/40 px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-white hover:shadow-md"
              data-testid={`home-link-${l.id}`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{l.titulo}</span>
                {l.descricao && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{l.descricao}</span>}
              </span>
              <ExternalLink size={15} className="shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function HistoriaAcervo() {
  const albums = usePublicAlbums("historico");
  return (
    <div className="mt-14" data-testid="historia-acervo">
      <div className="flex items-center gap-3">
        <span className="h-px w-10 bg-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Acervo histórico do IMJ</span>
      </div>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Recortes de imprensa, certificados e registros reais da trajetória do instituto, organizados em álbuns.
        Toque em um álbum para ver as fotos e ampliar.
      </p>
      {albums === null ? null : albums.length === 0 ? (
        <div className="mt-8 border border-dashed border-border bg-secondary/40 p-8 text-center" data-testid="historia-acervo-empty">
          <p className="text-sm font-semibold">Estamos preparando o acervo histórico do IMJ.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Mais de 20 anos de memórias em música estão sendo organizados e serão publicados aqui em breve.
          </p>
        </div>
      ) : (
        <div className="mt-8">
          <PublicAlbums albums={albums} testId="historia-acervo-albums" />
        </div>
      )}
    </div>
  );
}

function Historia() {
  return (
    <section id="historia" className="bg-white py-20 lg:py-28" data-testid="historia-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <SectionHeading eyebrow="Nossa história" title="Duas décadas de música em Cabreúva" testId="historia-title" />
            <div className="mt-10 grid grid-cols-2 gap-px bg-border">
              <div className="bg-white py-4 pr-4">
                <p className="font-heading text-4xl font-extrabold text-primary">2006</p>
                <p className="mt-1 text-sm text-muted-foreground">Fundação do instituto</p>
              </div>
              <div className="bg-white py-4 pl-4">
                <p className="font-heading text-4xl font-extrabold text-primary">2026</p>
                <p className="mt-1 text-sm text-muted-foreground">20 anos de trajetória</p>
              </div>
            </div>
          </div>
          <div className="space-y-5 text-base leading-relaxed text-zinc-700" data-testid="historia-text">
            <p>
              O <strong>Instituto Musical Jacaré — IMJ</strong> nasceu em agosto de 2006, fundado pelo maestro{" "}
              <strong>Lucas Resende da Silva</strong>, no bairro Jacaré, em Cabreúva/SP. Há duas décadas a escola forma
              músicos com aulas individuais e personalizadas — cada aluno aprende no seu ritmo, do primeiro acorde ao
              palco.
            </p>
            <p className="text-sm text-zinc-500">
              A trajetória completa está no acervo histórico abaixo.
            </p>
          </div>
        </div>
        <HistoriaAcervo />
      </div>
    </section>
  );
}

function VinteAnos() {
  return (
    <section id="20anos" className="bg-[#0A0A0A] py-20 text-white lg:py-28" data-testid="anniversary-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">2006 — 2026</span>
            </div>
            <h2
              className="mt-4 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl"
              data-testid="anniversary-title"
            >
              20 anos de música em Cabreúva
            </h2>
            <div className="mt-8 space-y-6">
              <div className="flex gap-4" data-testid="anniversary-milestone-2006">
                <p className="w-16 shrink-0 font-heading text-2xl font-extrabold text-primary">2006</p>
                <p className="border-l border-zinc-800 pl-4 text-sm leading-relaxed text-zinc-300">
                  Em agosto, o músico e maestro <strong className="text-white">Lucas Resende da Silva</strong> funda o
                  Instituto Musical Jacaré no bairro Jacaré, em Cabreúva/SP.
                </p>
              </div>
              <div className="flex gap-4" data-testid="anniversary-milestone-2026">
                <p className="w-16 shrink-0 font-heading text-2xl font-extrabold text-primary">2026</p>
                <p className="border-l border-zinc-800 pl-4 text-sm leading-relaxed text-zinc-300">
                  O IMJ completa <strong className="text-white">20 anos</strong> de atuação contínua na educação
                  musical, com aulas individuais e personalizadas — e com o projeto social{" "}
                  <strong className="text-white">Música para o Bem</strong>, que leva a música como instrumento de
                  transformação para a comunidade.
                </p>
              </div>
            </div>
            <Button
              asChild
              className="mt-10 bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="anniversary-cta"
            >
              <a href="#interesse">Faça parte dessa história</a>
            </Button>
          </div>
          <div className="relative" data-testid="anniversary-photo-slot">
            <img
              src="/assets/historia/honra-merito-camara-2011.webp"
              alt="Certificado de Honra ao Mérito concedido a Lucas Resende pela Câmara Municipal de Cabreúva em 2011 — acervo histórico do IMJ"
              loading="lazy"
              className="w-full border border-zinc-800 object-cover"
            />
            <p className="mt-3 text-xs text-zinc-500">
              Honra ao Mérito — Câmara Municipal de Cabreúva, 2011 · Acervo histórico do IMJ
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Momentos() {
  const albums = usePublicAlbums("momentos");
  if (!albums || albums.length === 0) return null;
  return (
    <section id="momentos" className="bg-white py-20 lg:py-28" data-testid="momentos-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading eyebrow="Dia a dia do instituto" title="Momentos no IMJ" testId="momentos-title" />
        <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
          Aulas, ensaios, apresentações, eventos e bastidores — o IMJ em movimento.
        </p>
        <div className="mt-10">
          <PublicAlbums albums={albums} testId="momentos-albums" />
        </div>
      </div>
    </section>
  );
}

function FloatingWhatsApp({ settings }) {
  const waDigits = (settings?.whatsapp || "").replace(/\D/g, "");
  if (!waDigits) return null;
  const waLink = `https://wa.me/${waDigits.startsWith("55") ? waDigits : `55${waDigits}`}`;
  return (
    <a
      href={waLink}
      target="_blank"
      rel="noreferrer"
      aria-label="Conversar com o IMJ no WhatsApp"
      title="Falar no WhatsApp"
      className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_4px_20px_rgba(245,166,35,0.4)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      data-testid="floating-whatsapp-button"
    >
      <span className="absolute inset-0 animate-ping rounded-full bg-primary/40 [animation-duration:2s] [animation-iteration-count:3]" aria-hidden="true" />
      <MessageCircle size={26} className="relative" />
    </a>
  );
}

function Contato({ settings }) {
  const waDigits = (settings?.whatsapp || "").replace(/\D/g, "");
  const waLink = waDigits ? `https://wa.me/${waDigits.startsWith("55") ? waDigits : `55${waDigits}`}` : "#";
  return (
    <footer id="contato" className="bg-[#0A0A0A] text-white" data-testid="contato-section">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            Instituto Musical Jacaré — 20 anos formando músicos em Cabreúva/SP.
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Endereço</p>
          <p className="mt-3 flex items-start gap-2 text-sm text-zinc-300" data-testid="footer-address">
            <MapPin size={16} className="mt-0.5 shrink-0 text-primary" />
            {settings?.address || "Rua Luiz Nunes, 36 — Bairro Jacaré, Cabreúva/SP"}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Contato</p>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            aria-label="Conversar com o IMJ no WhatsApp"
            className="mt-3 flex items-center gap-2 text-sm text-zinc-300 transition-colors hover:text-primary"
            data-testid="footer-whatsapp-link"
          >
            <MessageCircle size={16} className="text-primary" />
            {settings?.whatsapp || "(11) 99821-6246"}
          </a>
          {settings?.payment_methods && (
            <p className="mt-4 text-xs text-zinc-500" data-testid="footer-payments">
              Pagamento: {settings.payment_methods.join(" · ")}
            </p>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Navegação</p>
          <nav className="mt-3 flex flex-col gap-2">
            {NAV_LINKS.slice(1, 5).map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm text-zinc-300 transition-colors hover:text-primary"
                data-testid={`footer-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/acesso"
              className="mt-2 text-sm font-semibold text-zinc-400 transition-colors hover:text-primary"
              data-testid="footer-login-link"
            >
              Área restrita
            </Link>
          </nav>
        </div>
      </div>
      <div className="border-t border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500">
        © 2026 Instituto Musical Jacaré — IMJ · Cabreúva/SP
      </div>
    </footer>
  );
}

export default function PublicHome() {
  const [data, setData] = useState({ courses: [], modalities: [], teachers: [], settings: null });
  const [selection, setSelection] = useState({ courseId: "", modalityId: "", schedule: "" });

  useEffect(() => {
    Promise.all([
      api.get("/public/courses"),
      api.get("/public/modalities"),
      api.get("/public/teachers"),
      api.get("/public/settings"),
    ])
      .then(([c, m, t, s]) =>
        setData({ courses: c.data, modalities: m.data, teachers: t.data, settings: s.data })
      )
      .catch(() => {});
  }, []);

  const selectCourse = (course) => {
    setSelection((p) => ({ ...p, courseId: course.id, schedule: "" }));
    document.getElementById("horarios")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-home">
      <PublicHeader />
      <main>
        <Hero courses={data.courses} />
        <PublicMemories />
        <PublicCatalog
          courses={data.courses}
          modalities={data.modalities}
          teachers={data.teachers}
          settings={data.settings}
          onSelectCourse={selectCourse}
        />
        <Historia />
        <LinksUteis />
        <VinteAnos />
        <Momentos />
        <PublicInterest
          courses={data.courses}
          modalities={data.modalities}
          settings={data.settings}
          selection={selection}
          setSelection={setSelection}
        />
      </main>
      <Contato settings={data.settings} />
      <FloatingWhatsApp settings={data.settings} />
    </div>
  );
}
